/**
 * Notice:
 *  This file is basically Link.js from react-router, but modified for the purpose of SEO.
 *  Links created with this class will appear as anchors with absolute URLs in the DOM.
 */

import React, { forwardRef } from 'react';
import { __RouterContext as RouterContext } from 'react-router';
import PropTypes from 'prop-types';
import invariant from 'tiny-invariant';
import styled from 'styled-components';
import { createLocation } from 'history';

const resolveToLocation = (to, currentLocation) => typeof to === 'function' ? to(currentLocation) : to;
const normalizeToLocation = (to, currentLocation) => {
  return typeof to === 'string' ? createLocation(to, null, null, currentLocation) : to;
};

const isModifiedEvent = event =>
  !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);

/**
 * A history-aware <a> that behaves just like <Link>, except for the fact that it
 * will generate an <a> with an absolute path.
 */
const LinkAnchor = forwardRef(
  ({ navigate, onClick, openNewTab, className, underline, flex, style, children, ...rest }, forwardedRef) => {
    const { target } = rest;
    const augmentedStyle = { ...(style || {}), lineHeight: '1em' };
    const augmentedClassName = (className || '') + (underline ? 'underline-on-hover' : '');
    const handleDragStart = (event) => {
      event.preventDefault(); // Prevent the default drag behavior
    };
    const props = {
      ...rest,
      flex,
      style: augmentedStyle,
      className: augmentedClassName,
      target: openNewTab ? '_blank' : '_self',
      rel: 'noopener noreferrer',
      onDragStart: handleDragStart,
      onClick: event => {
        try {
          if (onClick) onClick(event);
        } catch (ex) {
          event.preventDefault();
          throw ex;
        }
        if (!event.defaultPrevented && // onClick prevented default
          event.button === 0 && // ignore everything but left clicks
          (!target || target === '_self') && // let browser handle "target=_blank" etc.
          !isModifiedEvent(event) && // ignore clicks with modifier keys
          !openNewTab) { // Navigation in the current tab should be ignored if we are supposed to open a new tab.
          event.preventDefault();
          navigate();
        }
      }
    };
    props.ref = forwardedRef;
    return (
      <Anchor {...props}>
        {children}
      </Anchor>
    );
  });

if (process.env.NODE_ENV !== 'production') {
  LinkAnchor.displayName = 'LinkAnchor';
}

/**
* The public API for rendering a history-aware <a>.
*/
const SeoLink = forwardRef(
  ({ component = LinkAnchor, replace, to, ...rest }, forwardedRef) => {
    return (
      <RouterContext.Consumer>
        {context => {
          invariant(context, 'You should not use <Link> outside a <Router>');
          const { history } = context;

          const location = normalizeToLocation(resolveToLocation(to, context.location), context.location);
          const relativeHref = location ? history.createHref(location) : '';

          // This is the part where we generate an absolute URL. The href is only for presentational purposes because
          // when the link is clicked, the 'to' prop of this component is used to determine where to go.
          const { protocol, hostname, port } = window.location;
          const absoluteHrefPrefix = `${protocol}//${hostname}` + (port ? `:${port}` : '');
          const absoluteHref = absoluteHrefPrefix + relativeHref;

          const props = {
            ...rest,
            href: absoluteHref,
            navigate() {
              const location = resolveToLocation(to, context.location);
              const method = replace ? history.replace : history.push;
              method(location);
            }
          };
          props.ref = forwardedRef;
          return React.createElement(component, props);
        }}
      </RouterContext.Consumer>
    );
  });

if (process.env.NODE_ENV !== 'production') {
  const toType = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.func
  ]);
  SeoLink.displayName = 'SeoLink';
  SeoLink.propTypes = {
    onClick: PropTypes.func,
    replace: PropTypes.bool,
    target: PropTypes.string,
    to: toType.isRequired,
    /* Custom props added by Lemonade team */
    openNewTab: PropTypes.bool,
    className: PropTypes.string,
    flex: PropTypes.bool,
    underline: PropTypes.bool,
    style: PropTypes.shape({})
  };
}

SeoLink.defaultProps = {
  replace: false,
  openNewTab: false,
  className: '',
  flex: false,
  underline: false,
  style: {}
};

const Anchor = styled.a`
  ${props => props.flex && 'display: flex; align-items: center; justify-content: center'};
`;

export default SeoLink;
